import { jsPDF } from "jspdf";
import { logoBase64 } from "@/utils/pdfLogo";
import { eSignatureLee, eSignatureMelanie } from "@/utils/eSignatureDoctors.js";

import moment from "moment";

export default function pdfGenerateHandler() {
  const exportPDF = async (payload = {}) => {
    const patient = payload.patient || {};

    const prescriptions = Array.isArray(payload.prescriptions)
      ? payload.prescriptions
      : [];

    if (!prescriptions.length) {
      alert("No prescription available.");
      return;
    }

    const doc = new jsPDF({
      compress: true,
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const bottomMargin = 20;

    let y = 130;

    const setDefaultStyle = () => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(30, 30, 30);
    };

    const addHeader = () => {
      setDefaultStyle();

      const logoWidth = 90;
      const logoHeight = 22;

      const logoX = (pageWidth - logoWidth) / 2;

      doc.addImage(
        logoBase64,
        "JPEG",
        logoX,
        10,
        logoWidth,
        logoHeight,
        "",
        "FAST",
      );

      doc.setFont("helvetica", "normal");

      doc.setFontSize(13);

      doc.text(patient.doctor || "-", pageWidth / 2, 40, {
        align: "center",
      });

      doc.setFontSize(11);

      doc.text(patient.specialization || "-", pageWidth / 2, 47, {
        align: "center",
      });

      doc.setFont("helvetica", "bold");

      doc.setFontSize(17);

      doc.text("P  R  E  S  C  R  I  P  T  I  O  N", pageWidth / 2, 68, {
        align: "center",
      });

      doc.setFont("helvetica", "normal");

      doc.setFontSize(11);

      doc.text(`Patient Name: ${patient.membername || "-"}`, 10, 85);

      doc.text(
        `Date: ${
          patient.createdat ? moment(patient.createdat).format("LL") : "N/A"
        }`,
        pageWidth - 10,
        85,
        {
          align: "right",
        },
      );

      doc.text(
        `Birthdates: ${
          patient.birthdate ? moment(patient.birthdate).format("LL") : "-"
        }`,
        10,
        95,
      );

      doc.setFont("helvetica", "bold");

      doc.setFontSize(32);

      doc.text("R", 10, 112);

      doc.setFontSize(28);

      doc.text("x", 18, 112);

      setDefaultStyle();
    };

    const addContinuationHeader = () => {
      setDefaultStyle();

      const logoWidth = 60;
      const logoHeight = 15;

      const logoX = (pageWidth - logoWidth) / 2;

      doc.addImage(
        logoBase64,
        "JPEG",
        logoX,
        10,
        logoWidth,
        logoHeight,
        "",
        "FAST",
      );

      doc.setFont("helvetica", "bold");

      doc.setFontSize(13);

      doc.text("PRESCRIPTION CONTINUATION", pageWidth / 2, 30, {
        align: "center",
      });

      doc.setDrawColor(150);

      doc.setLineWidth(0.3);

      doc.line(10, 35, pageWidth - 10, 35);

      doc.setFont("helvetica", "normal");

      doc.setFontSize(11);

      doc.text(`Patient Name: ${patient.membername || "-"}`, 10, 45);

      doc.text(
        `Date: ${
          patient.createdat ? moment(patient.createdat).format("LL") : "-"
        }`,
        pageWidth - 10,
        45,
        {
          align: "right",
        },
      );

      doc.setFont("helvetica", "bold");

      doc.setFontSize(32);

      doc.text("R", 10, 60);

      doc.setFontSize(28);

      doc.text("x", 18, 60);

      setDefaultStyle();
    };

    const addFooter = (pageNumber, totalPages) => {
      doc.setFont("helvetica", "normal");

      doc.setFontSize(10);

      doc.setTextColor(100);

      doc.text(
        `Page ${pageNumber} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        {
          align: "center",
        },
      );

      setDefaultStyle();
    };

    addHeader();

    const lineGap = 7;

    setDefaultStyle();

    const addNewPage = () => {
      doc.addPage();

      addContinuationHeader();

      y = 75;
    };

    prescriptions.forEach((pres) => {
      if (y + 35 > pageHeight - bottomMargin) {
        addNewPage();
      }

      doc.text(
        `${pres.generic_name || "-"} (${pres.brand_name || "-"})`,
        25,
        y,
      );

      y += lineGap;

      doc.setFontSize(11);

      doc.text(
        `${pres.dose || "-"} | #${pres.quantity || "-"} | for ${
          pres.number_of_days || "-"
        } day(s)`,
        30,
        y,
      );

      y += lineGap;

      doc.text(`SIG: ${pres.sig || "-"}`, 30, y);

      y += 14;

      setDefaultStyle();
    });

    if (y + 40 > pageHeight - bottomMargin) {
      addNewPage();
    }

    const signatureY = Math.min(y + 35, pageHeight - 55);

    const lineWidth = 80;

    const marginRight = 15;

    const lineX = pageWidth - lineWidth - marginRight;

    const marginX = lineX + lineWidth / 2 - 15;

    const sigStartY = signatureY - 25;

    if (patient.doctorid === 1) {
      doc.addImage(eSignatureLee, "PNG", marginX, sigStartY, 30, 35);
    }

    if (patient.doctorid === 13) {
      doc.addImage(eSignatureMelanie, "PNG", marginX, sigStartY, 30, 35);
    }

    doc.setFont("helvetica", "bold");

    doc.setFontSize(14);

    doc.text(patient.doctor || "-", lineX + lineWidth / 2, signatureY, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");

    doc.setFontSize(12);

    doc.text(
      `License No: ${prescriptions[0].license_number || "-"}`,
      lineX + lineWidth / 2,
      signatureY + 8,
      {
        align: "center",
      },
    );

    doc.text(
      `PTR No: ${prescriptions[0].ptr_number || "-"}`,
      lineX + lineWidth / 2,
      signatureY + 14,
      {
        align: "center",
      },
    );

    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      addFooter(i, totalPages);
    }

    const transactionId = patient.transactionid || Date.now();

    doc.save(`PRESCRIPTION_${transactionId}.pdf`);
  };

  return { exportPDF };
}
